import React from 'react';
import { motion } from 'framer-motion';
import { Brain } from 'lucide-react';

interface StressGaugeProps {
  value: number;
  label: string;
  lastUpdated: string;
}

export function StressGauge({ value, label, lastUpdated }: StressGaugeProps) {
  // Convert value to angle (0-3 scale to 0-180 degrees)
  const angle = (value / 3) * 180;
  const getColor = (value: number) => {
    if (value < 1) return 'text-success';
    if (value < 2) return 'text-primary';
    return 'text-warning';
  };

  return (
    <div className="relative w-48 h-24 mx-auto">
      {/* Gauge Background */}
      <svg className="w-full h-full" viewBox="0 0 200 100">
        <path
          d="M20 80 A 60 60 0 0 1 180 80"
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth="4"
          strokeLinecap="round"
        />
        {/* Active Arc */}
        <motion.path
          d="M20 80 A 60 60 0 0 1 180 80"
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
          className={getColor(value)}
          initial={{ pathLength: 0 }}
          animate={{ pathLength: value / 3 }}
          transition={{ duration: 1, ease: "easeOut" }}
        />
        {/* Needle */}
        <motion.line
          x1="100"
          y1="80"
          x2="100"
          y2="30"
          stroke="hsl(var(--foreground))"
          strokeWidth="2"
          initial={{ transform: 'rotate(0deg)' }}
          animate={{ transform: `rotate(${angle - 90}deg)` }}
          style={{ transformOrigin: '100px 80px' }}
          transition={{ duration: 1, ease: "easeOut" }}
        />
        <circle cx="100" cy="80" r="4" fill="hsl(var(--foreground))" />
      </svg>
      
      {/* Value Display */}
      <div className="absolute inset-x-0 top-8 text-center">
        <div className="text-3xl font-bold">{value.toFixed(1)}</div>
        <div className="text-sm text-muted-foreground">{label}</div>
        <div className="text-xs text-muted-foreground mt-1">Last updated {lastUpdated}</div>
      </div>
    </div>
  );
}